import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, X, Eye, AlertTriangle, Loader2 } from "lucide-react";
import { api, API, apiErr } from "@/lib/api";

export const JURISDICTIONS = ["EU", "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE"];

export function jurisdictionLabel(code, lang = "en") {
  if (!code) return "";
  if (code === "EU") return { hu: "Európai Unió", de: "Europäische Union", fr: "Union européenne", cs: "Evropská unie" }[lang] || "European Union";
  try { return `${new Intl.DisplayNames([lang], { type: "region" }).of(code)} (${code})`; } catch { return code; }
}

const fileHref = (u) => (u?.startsWith("/api/") ? `${API}${u.slice(4)}` : u);

export function DisputeMeta({ dispute, onSave }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const f = dispute.final_decision_file;
  const phase5 = (dispute.phase || 1) >= 5 || dispute.not_eligible;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onSave({ final_decision_file: r.data });
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail) || "Feltöltés sikertelen");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="border-b border-zinc-800/70 bg-zinc-950/20 px-4 py-2" data-testid="dispute-meta">
      <div className="flex flex-wrap items-center gap-3 text-[12px]">
        <span className="flex items-center gap-1.5 text-zinc-400" data-testid="dispute-last-visit">
          <Eye size={13} className="text-zinc-500" /> Utolsó látogatás:
          <span className="font-mono text-zinc-200">{dispute.last_visit_at ? new Date(dispute.last_visit_at).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", hour12: false, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "még nem látogatta"}</span>
          {dispute.last_visit_ip && <span className="text-zinc-600">· {dispute.last_visit_ip}</span>}
        </span>
        <label className="flex items-center gap-1.5 text-zinc-400">
          Joghatóság
          <select data-testid="dispute-jurisdiction" value={dispute.jurisdiction || ""} onChange={(e) => onSave({ jurisdiction: e.target.value })}
            className="h-7 rounded-md border border-zinc-800 bg-zinc-950 px-2 text-[12px] text-zinc-100">
            <option value="">—</option>
            {JURISDICTIONS.map((c) => <option key={c} value={c}>{jurisdictionLabel(c, "hu")}</option>)}
          </select>
        </label>
        <div className="ml-auto flex items-center gap-2">
          {f ? (
            <span className="flex items-center gap-1.5 rounded-md border border-blue-500/30 bg-blue-500/10 px-2 py-1 text-blue-300" data-testid="dispute-final-file">
              <FileText size={13} />
              <a href={fileHref(f.url)} target="_blank" rel="noreferrer" className="max-w-[180px] truncate hover:underline">{f.filename}</a>
              <button data-testid="dispute-final-file-remove" onClick={() => onSave({ remove_final_file: true })} className="text-zinc-400 hover:text-red-400"><X size={13} /></button>
            </span>
          ) : null}
          <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="dispute-final-file-input" />
          <button data-testid="dispute-final-file-upload" disabled={!phase5 || uploading} onClick={() => inputRef.current?.click()}
            title={phase5 ? "" : "Csak az 5. fázisban (vagy NEM jogosult esetén) tölthető fel"}
            className="flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-zinc-300 hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-40">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Végső határozat feltöltése
          </button>
        </div>
      </div>
      {dispute.escalation && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-[12px] text-yellow-200" data-testid="dispute-escalation-box">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-yellow-400" />
          <div>
            <div className="font-semibold">Eszkalálva a bejelentő által · {new Date(dispute.escalation.created_at).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", hour12: false })}</div>
            <p className="mt-0.5 whitespace-pre-wrap text-yellow-100/80">{dispute.escalation.reason}</p>
          </div>
        </div>
      )}
    </div>
  );
}
